// apps/accounts/src/pages/SiteMeasurements.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../utils/dateHelpers';
import pb from '../lib/pocketbaseClient';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';
import { 
  Plus, Search, Edit2, Trash2, X, Copy, Save, FolderPlus, 
  ChevronDown, ChevronRight, Calendar, Building2, Users,
  TrendingUp, TrendingDown, DollarSign, MinusCircle, Eye, EyeOff,
  CheckSquare, Square, Trash, PieChart
} from 'lucide-react';

// Helper function to format numbers to 2 decimal places
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return 0;
  return parseFloat(parseFloat(num).toFixed(2));
};

// Helper function to format number for display
const formatDisplayNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return '0.00';
  return parseFloat(num).toFixed(2);
};

export default function SiteMeasurements() {
  // States
  const [measurements, setMeasurements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableRates, setAvailableRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Filter states
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);

  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Common data (Date, Project, Vendor)
  const [commonData, setCommonData] = useState({
    measurementDate: new Date().toISOString().split('T')[0],
    project: '',
    vendor: '',
  });

  // Categories with their measurements
  const [categoriesData, setCategoriesData] = useState([
    {
      id: Date.now(),
      category: '',
      clientRate: '',
      vendorRate: '',
      entries: [
        {
          id: Date.now() + Math.random(),
          length: '',
          width: '',
          height: '',
          deductionLength: '',
          deductionWidth: '',
          deductionHeight: '',
          showDeduction: false,
          description: '',
          totalQuantity: 0,
          deductionQuantity: 0,
          netQuantity: 0,
          unit: '',
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0,
        }
      ]
    }
  ]);

  // Expanded groups for view
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [measurementsRes, projectsRes, vendorsRes, categoriesRes, ratesRes] = await Promise.all([
        pb.collection('site_measurements').getFullList({
          sort: '-measurementDate',
          expand: 'project,vendor,category'
        }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('work_categories').getFullList({ sort: 'name' }),
        pb.collection('project_vendor_rates').getFullList({
          expand: 'project,vendor,category'
        }),
      ]);

      // Format all numbers to 2 decimal places
      const formattedMeasurements = measurementsRes.map(m => ({
        ...m,
        length: formatNumber(m.length),
        width: formatNumber(m.width),
        height: formatNumber(m.height),
        deductionLength: formatNumber(m.deductionLength),
        deductionWidth: formatNumber(m.deductionWidth),
        deductionHeight: formatNumber(m.deductionHeight),
        totalQuantity: formatNumber(m.totalQuantity),
        deductionQuantity: formatNumber(m.deductionQuantity),
        netQuantity: formatNumber(m.netQuantity),
        clientRate: formatNumber(m.clientRate),
        vendorRate: formatNumber(m.vendorRate),
        clientAmount: formatNumber(m.clientAmount),
        vendorAmount: formatNumber(m.vendorAmount),
        profit: formatNumber(m.profit),
      }));

      setMeasurements(formattedMeasurements);
      setProjects(projectsRes);
      setVendors(vendorsRes);
      setCategories(categoriesRes);
      setAvailableRates(ratesRes);
      setSelectedItems([]);
      setSelectAll(false);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate single entry with deduction - returns formatted numbers
  const calculateSingleEntry = (categoryId, entry) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return { total: 0, deduction: 0, net: 0, unit: '' };

    const unit = category.unit;
    let total = 0;
    let deduction = 0;
    
    const len = parseFloat(entry.length) || 0;
    const wid = parseFloat(entry.width) || 0;
    const hgt = parseFloat(entry.height) || 0;
    
    const dedLen = parseFloat(entry.deductionLength) || 0;
    const dedWid = parseFloat(entry.deductionWidth) || 0;
    const dedHgt = parseFloat(entry.deductionHeight) || 0;

    if (unit === 'sqm' || unit === 'sqft') {
      total = len * hgt;
      deduction = dedLen * dedHgt;
    } else if (unit === 'cum' || unit === 'cft') {
      total = len * wid * hgt;
      deduction = dedLen * dedWid * dedHgt;
    } else if (unit === 'rmt' || unit === 'rft') {
      total = len;
      deduction = dedLen;
    }

    const unitDisplay = unit === 'sqm' ? 'sqm' : 
                        unit === 'sqft' ? 'sqft' : 
                        unit === 'cum' ? 'cum' : 
                        unit === 'cft' ? 'cft' : 
                        unit === 'rmt' ? 'rmt' : 'rft';

    return { 
      total: formatNumber(total), 
      deduction: formatNumber(deduction),
      net: formatNumber(total - deduction),
      unit: unitDisplay 
    };
  };

  // Update category calculations
  const updateCategoryCalculations = (categoryIndex) => {
    const cat = categoriesData[categoryIndex];
    const clientRate = formatNumber(cat.clientRate);
    const vendorRate = formatNumber(cat.vendorRate);

    const updatedEntries = cat.entries.map(entry => {
      const calc = calculateSingleEntry(cat.category, entry);
      const netQty = calc.net || 0;
      return {
        ...entry,
        totalQuantity: calc.total,
        deductionQuantity: calc.deduction,
        netQuantity: netQty,
        unit: calc.unit,
        clientAmount: formatNumber(netQty * clientRate),
        vendorAmount: formatNumber(netQty * vendorRate),
        profit: formatNumber((netQty * clientRate) - (netQty * vendorRate)),
      };
    });

    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex].entries = updatedEntries;
    setCategoriesData(updatedCategories);
  };

  // Toggle deduction visibility
  const toggleDeduction = (categoryIndex, entryIndex) => {
    const updatedCategories = [...categoriesData];
    const currentShow = updatedCategories[categoryIndex].entries[entryIndex].showDeduction || false;
    updatedCategories[categoryIndex].entries[entryIndex].showDeduction = !currentShow;
    
    if (currentShow) {
      updatedCategories[categoryIndex].entries[entryIndex].deductionLength = '';
      updatedCategories[categoryIndex].entries[entryIndex].deductionWidth = '';
      updatedCategories[categoryIndex].entries[entryIndex].deductionHeight = '';
    }
    
    setCategoriesData(updatedCategories);
    setTimeout(() => updateCategoryCalculations(categoryIndex), 100);
  };

  // Auto-load rates
  const loadRatesForCategory = (categoryIndex) => {
    const cat = categoriesData[categoryIndex];
    if (!commonData.project || !commonData.vendor || !cat.category) return;

    const matchedRate = availableRates.find(r =>
      r.project === commonData.project &&
      r.vendor === commonData.vendor &&
      r.category === cat.category
    );

    const updatedCategories = [...categoriesData];
    if (matchedRate) {
      updatedCategories[categoryIndex].clientRate = formatNumber(matchedRate.clientRate);
      updatedCategories[categoryIndex].vendorRate = formatNumber(matchedRate.vendorRate);
    } else {
      const category = categories.find(c => c.id === cat.category);
      if (category) {
        updatedCategories[categoryIndex].clientRate = formatNumber(category.defaultClientRate);
        updatedCategories[categoryIndex].vendorRate = formatNumber(category.defaultVendorRate);
      }
    }
    setCategoriesData(updatedCategories);
    setTimeout(() => updateCategoryCalculations(categoryIndex), 100);
  };

  // Handle common data change
  const handleCommonChange = (e) => {
    const { name, value } = e.target;
    setCommonData(prev => ({ ...prev, [name]: value }));
    setModalError('');
  };

  // Handle category change
  const handleCategoryChange = (categoryIndex, value) => {
    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex].category = value;
    setCategoriesData(updatedCategories);
    setTimeout(() => loadRatesForCategory(categoryIndex), 100);
  };

  // Handle entry change
  const handleEntryChange = (categoryIndex, entryIndex, field, value) => {
    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex].entries[entryIndex] = {
      ...updatedCategories[categoryIndex].entries[entryIndex],
      [field]: value,
    };
    setCategoriesData(updatedCategories);
    setTimeout(() => updateCategoryCalculations(categoryIndex), 100);
  };

  // Handle rate change
  const handleRateChange = (categoryIndex, field, value) => {
    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex][field] = value;
    setCategoriesData(updatedCategories);
    setTimeout(() => updateCategoryCalculations(categoryIndex), 100);
  };

  // Add new category
  const addCategory = () => {
    const newCategory = {
      id: Date.now() + Math.random(),
      category: '',
      clientRate: '',
      vendorRate: '',
      entries: [
        {
          id: Date.now() + Math.random(),
          length: '',
          width: '',
          height: '',
          deductionLength: '',
          deductionWidth: '',
          deductionHeight: '',
          showDeduction: false,
          description: '',
          totalQuantity: 0,
          deductionQuantity: 0,
          netQuantity: 0,
          unit: '',
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0,
        }
      ]
    };
    setCategoriesData(prev => [...prev, newCategory]);
  };

  // Remove category
  const removeCategory = (categoryIndex) => {
    if (categoriesData.length <= 1) {
      toast.error('At least one category is required');
      return;
    }
    setCategoriesData(categoriesData.filter((_, i) => i !== categoryIndex));
  };

  // Add entry to category
  const addEntryToCategory = (categoryIndex) => {
    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex].entries.push({
      id: Date.now() + Math.random(),
      length: '',
      width: '',
      height: '',
      deductionLength: '',
      deductionWidth: '',
      deductionHeight: '',
      showDeduction: false,
      description: '',
      totalQuantity: 0,
      deductionQuantity: 0,
      netQuantity: 0,
      unit: '',
      clientAmount: 0,
      vendorAmount: 0,
      profit: 0,
    });
    setCategoriesData(updatedCategories);
  };

  // Remove entry from category
  const removeEntryFromCategory = (categoryIndex, entryIndex) => {
    const cat = categoriesData[categoryIndex];
    if (cat.entries.length <= 1) {
      toast.error('At least one entry is required per category');
      return;
    }
    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex].entries = updatedCategories[categoryIndex].entries.filter((_, i) => i !== entryIndex);
    setCategoriesData(updatedCategories);
    setTimeout(() => updateCategoryCalculations(categoryIndex), 100);
  };

  // Copy entry
  const copyEntry = (categoryIndex, entryIndex) => {
    const cat = categoriesData[categoryIndex];
    const entryToCopy = cat.entries[entryIndex];
    const newEntry = {
      ...entryToCopy,
      id: Date.now() + Math.random(),
      length: '',
      width: '',
      height: '',
      deductionLength: '',
      deductionWidth: '',
      deductionHeight: '',
      showDeduction: false,
      totalQuantity: 0,
      deductionQuantity: 0,
      netQuantity: 0,
      clientAmount: 0,
      vendorAmount: 0,
      profit: 0,
    };
    const updatedCategories = [...categoriesData];
    updatedCategories[categoryIndex].entries.splice(entryIndex + 1, 0, newEntry);
    setCategoriesData(updatedCategories);
  };

  // Get category total with formatted numbers
  const getCategoryTotal = (cat) => {
    return cat.entries.reduce((acc, entry) => ({
      totalQuantity: formatNumber(acc.totalQuantity + (entry.totalQuantity || 0)),
      deductionQuantity: formatNumber(acc.deductionQuantity + (entry.deductionQuantity || 0)),
      netQuantity: formatNumber(acc.netQuantity + (entry.netQuantity || 0)),
      clientAmount: formatNumber(acc.clientAmount + (entry.clientAmount || 0)),
      vendorAmount: formatNumber(acc.vendorAmount + (entry.vendorAmount || 0)),
      profit: formatNumber(acc.profit + (entry.profit || 0)),
    }), { 
      totalQuantity: 0, 
      deductionQuantity: 0,
      netQuantity: 0,
      clientAmount: 0, 
      vendorAmount: 0, 
      profit: 0 
    });
  };

  // Get grand total for modal with formatted numbers
  const getModalGrandTotal = () => {
    return categoriesData.reduce((acc, cat) => {
      const total = getCategoryTotal(cat);
      return {
        totalQuantity: formatNumber(acc.totalQuantity + total.totalQuantity),
        deductionQuantity: formatNumber(acc.deductionQuantity + total.deductionQuantity),
        netQuantity: formatNumber(acc.netQuantity + total.netQuantity),
        clientAmount: formatNumber(acc.clientAmount + total.clientAmount),
        vendorAmount: formatNumber(acc.vendorAmount + total.vendorAmount),
        profit: formatNumber(acc.profit + total.profit),
      };
    }, { 
      totalQuantity: 0, 
      deductionQuantity: 0,
      netQuantity: 0,
      clientAmount: 0, 
      vendorAmount: 0, 
      profit: 0 
    });
  };

  // Open modal for new entry
  const openNewModal = () => {
    setEditingEntry(null);
    setEditMode(false);
    setEditData(null);
    setCommonData({
      measurementDate: new Date().toISOString().split('T')[0],
      project: '',
      vendor: '',
    });
    setCategoriesData([
      {
        id: Date.now(),
        category: '',
        clientRate: '',
        vendorRate: '',
        entries: [
          {
            id: Date.now() + Math.random(),
            length: '',
            width: '',
            height: '',
            deductionLength: '',
            deductionWidth: '',
            deductionHeight: '',
            showDeduction: false,
            description: '',
            totalQuantity: 0,
            deductionQuantity: 0,
            netQuantity: 0,
            unit: '',
            clientAmount: 0,
            vendorAmount: 0,
            profit: 0,
          }
        ]
      }
    ]);
    setModalError('');
    setModalSuccess('');
    setShowModal(true);
  };

  // Open modal for edit
  const openEditModal = (measurement) => {
    setEditingEntry(measurement);
    setEditMode(true);
    setEditData(measurement);
    
    setCommonData({
      measurementDate: measurement.measurementDate || '',
      project: measurement.project || '',
      vendor: measurement.vendor || '',
    });
    
    setCategoriesData([
      {
        id: Date.now(),
        category: measurement.category || '',
        clientRate: formatNumber(measurement.clientRate),
        vendorRate: formatNumber(measurement.vendorRate),
        entries: [
          {
            id: Date.now() + Math.random(),
            length: measurement.length || '',
            width: measurement.width || '',
            height: measurement.height || '',
            deductionLength: measurement.deductionLength || '',
            deductionWidth: measurement.deductionWidth || '',
            deductionHeight: measurement.deductionHeight || '',
            showDeduction: measurement.deductionLength || measurement.deductionWidth || measurement.deductionHeight ? true : false,
            description: measurement.description || '',
            totalQuantity: formatNumber(measurement.totalQuantity),
            deductionQuantity: formatNumber(measurement.deductionQuantity),
            netQuantity: formatNumber(measurement.netQuantity),
            unit: measurement.unit || '',
            clientAmount: formatNumber(measurement.clientAmount),
            vendorAmount: formatNumber(measurement.vendorAmount),
            profit: formatNumber(measurement.profit),
          }
        ]
      }
    ]);
    
    setModalError('');
    setModalSuccess('');
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingEntry(null);
    setEditMode(false);
    setEditData(null);
    setModalError('');
    setModalSuccess('');
  };

  // Submit all entries from modal
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setModalError('');
    setModalSuccess('');

    try {
      if (!commonData.project) {
        setModalError('Please select a project');
        setSubmitting(false);
        return;
      }
      if (!commonData.vendor) {
        setModalError('Please select a vendor');
        setSubmitting(false);
        return;
      }

      const projectExists = projects.find(p => p.id === commonData.project);
      const vendorExists = vendors.find(v => v.id === commonData.vendor);
      
      if (!projectExists) {
        setModalError('Selected project does not exist');
        setSubmitting(false);
        return;
      }
      if (!vendorExists) {
        setModalError('Selected vendor does not exist');
        setSubmitting(false);
        return;
      }

      let hasError = false;
      let errorMsg = '';
      
      for (let ci = 0; ci < categoriesData.length; ci++) {
        const cat = categoriesData[ci];
        
        if (!cat.category) {
          errorMsg = `Category ${ci + 1}: Please select a category`;
          hasError = true;
          break;
        }
        
        const categoryExists = categories.find(c => c.id === cat.category);
        if (!categoryExists) {
          errorMsg = `Category ${ci + 1}: Selected category does not exist`;
          hasError = true;
          break;
        }
        
        if (!cat.clientRate || parseFloat(cat.clientRate) <= 0) {
          errorMsg = `Category ${ci + 1}: Please enter valid client rate`;
          hasError = true;
          break;
        }
        if (!cat.vendorRate || parseFloat(cat.vendorRate) <= 0) {
          errorMsg = `Category ${ci + 1}: Please enter valid vendor rate`;
          hasError = true;
          break;
        }
        
        let hasValidEntry = false;
        for (let ei = 0; ei < cat.entries.length; ei++) {
          const entry = cat.entries[ei];
          
          if (!entry.length && !entry.width && !entry.height) continue;
          
          const lenValue = parseFloat(entry.length);
          if (isNaN(lenValue) || lenValue <= 0) {
            errorMsg = `Category ${ci + 1}, Entry ${ei + 1}: Please enter valid length`;
            hasError = true;
            break;
          }
          
          const category = categories.find(c => c.id === cat.category);
          if (category) {
            const unit = category.unit;
            if (unit === 'cum' || unit === 'cft') {
              const widValue = parseFloat(entry.width);
              if (isNaN(widValue) || widValue <= 0) {
                errorMsg = `Category ${ci + 1}, Entry ${ei + 1}: Please enter valid width`;
                hasError = true;
                break;
              }
              const hgtValue = parseFloat(entry.height);
              if (isNaN(hgtValue) || hgtValue <= 0) {
                errorMsg = `Category ${ci + 1}, Entry ${ei + 1}: Please enter valid height`;
                hasError = true;
                break;
              }
            } else if (unit === 'sqm' || unit === 'sqft') {
              const hgtValue = parseFloat(entry.height);
              if (isNaN(hgtValue) || hgtValue <= 0) {
                errorMsg = `Category ${ci + 1}, Entry ${ei + 1}: Please enter valid height`;
                hasError = true;
                break;
              }
            }
          }
          
          if (entry.netQuantity <= 0) {
            errorMsg = `Category ${ci + 1}, Entry ${ei + 1}: Please enter valid measurements`;
            hasError = true;
            break;
          }
          hasValidEntry = true;
        }
        
        if (!hasValidEntry && !hasError) {
          errorMsg = `Category ${ci + 1}: Please add at least one valid measurement`;
          hasError = true;
          break;
        }
        
        if (hasError) break;
      }

      if (hasError) {
        setModalError(errorMsg);
        setSubmitting(false);
        return;
      }

      let savedCount = 0;
      for (const cat of categoriesData) {
        const categoryExists = categories.find(c => c.id === cat.category);
        if (!categoryExists) continue;
        
        for (const entry of cat.entries) {
          if (!entry.length && !entry.width && !entry.height) continue;
          
          const calc = calculateSingleEntry(cat.category, entry);
          const netQty = calc.net || 0;
          if (netQty <= 0) continue;
          
          const clientRate = formatNumber(cat.clientRate);
          const vendorRate = formatNumber(cat.vendorRate);
          
          const data = {
            measurementDate: commonData.measurementDate,
            project: commonData.project,
            vendor: commonData.vendor,
            category: cat.category,
            length: formatNumber(entry.length),
            width: formatNumber(entry.width),
            height: formatNumber(entry.height),
            deductionLength: formatNumber(entry.deductionLength),
            deductionWidth: formatNumber(entry.deductionWidth),
            deductionHeight: formatNumber(entry.deductionHeight),
            quantity: calc.total,
            deductionQuantity: calc.deduction,
            totalQuantity: netQty,
            clientRate: clientRate,
            vendorRate: vendorRate,
            clientAmount: formatNumber(netQty * clientRate),
            vendorAmount: formatNumber(netQty * vendorRate),
            profit: formatNumber((netQty * clientRate) - (netQty * vendorRate)),
            description: entry.description || '',
            createdBy: pb.authStore.model?.id,
          };

          try {
            if (editingEntry && editMode) {
              await pb.collection('site_measurements').update(editingEntry.id, data);
            } else {
              await pb.collection('site_measurements').create(data);
            }
            savedCount++;
          } catch (err) {
            console.error('Error saving entry:', err);
            throw err;
          }
        }
      }

      if (savedCount === 0) {
        setModalError('No valid measurements to save');
        setSubmitting(false);
        return;
      }

      const successMsg = editMode ? `${savedCount} measurement(s) updated successfully!` : `${savedCount} measurement(s) saved successfully!`;
      setModalSuccess(successMsg);
      toast.success(successMsg);
      
      setTimeout(() => {
        closeModal();
        fetchData();
      }, 1500);
      
    } catch (err) {
      console.error('Save error:', err);
      let errorMessage = err.message || 'Failed to save measurements';
      
      if (err.message && err.message.includes('relation')) {
        errorMessage = 'One or more selected items do not exist';
      } else if (err.data?.data) {
        const fieldErrors = Object.entries(err.data.data)
          .map(([key, val]) => `${key}: ${val.message}`)
          .join(', ');
        errorMessage = `Validation error: ${fieldErrors}`;
      }
      
      setModalError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete single measurement
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this measurement?')) return;
    try {
      await pb.collection('site_measurements').delete(id);
      toast.success('Measurement deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    }
  };

  // Bulk delete selected measurements
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to delete');
      return;
    }
    
    if (!window.confirm(`Delete ${selectedItems.length} selected measurement(s)?`)) return;
    
    try {
      let deletedCount = 0;
      for (const id of selectedItems) {
        await pb.collection('site_measurements').delete(id);
        deletedCount++;
      }
      toast.success(`${deletedCount} measurement(s) deleted successfully`);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete some items: ' + err.message);
      fetchData();
    }
  };

  // Toggle selection of a single item
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      const allIds = measurements.map(m => m.id);
      setSelectedItems(allIds);
    }
    setSelectAll(!selectAll);
  };

  // Get unit display
  const getUnitDisplay = (unit) => {
    return unit === 'sqm' ? 'sqm' : 
           unit === 'sqft' ? 'sqft' : 
           unit === 'cum' ? 'cum' : 
           unit === 'cft' ? 'cft' : 
           unit === 'rmt' ? 'rmt' : 'rft';
  };

  // Group measurements with formatted numbers
  const getGroupedMeasurements = () => {
    const filtered = measurements.filter(m => {
      const matchProject = filterProject === 'all' || m.project === filterProject;
      const matchVendor = filterVendor === 'all' || m.vendor === filterVendor;
      const matchCategory = filterCategory === 'all' || m.category === filterCategory;
      const matchSearch = m.expand?.project?.name?.toLowerCase().includes(search.toLowerCase()) ||
                          m.expand?.vendor?.name?.toLowerCase().includes(search.toLowerCase()) ||
                          m.expand?.category?.name?.toLowerCase().includes(search.toLowerCase());
      const matchDate = (!dateFrom || m.measurementDate >= dateFrom) && (!dateTo || m.measurementDate <= dateTo);
      return matchProject && matchVendor && matchCategory && matchSearch && matchDate;
    });

    const groups = {};
    filtered.forEach(m => {
      const key = `${m.measurementDate}_${m.project}_${m.vendor}`;
      if (!groups[key]) {
        groups[key] = {
          date: m.measurementDate,
          project: m.project,
          projectName: m.expand?.project?.name || 'N/A',
          vendor: m.vendor,
          vendorName: m.expand?.vendor?.name || 'N/A',
          items: [],
          totals: { clientAmount: 0, vendorAmount: 0, profit: 0 },
          categoryTotals: {}
        };
      }
      groups[key].items.push(m);
      groups[key].totals.clientAmount = formatNumber(groups[key].totals.clientAmount + (m.clientAmount || 0));
      groups[key].totals.vendorAmount = formatNumber(groups[key].totals.vendorAmount + (m.vendorAmount || 0));
      groups[key].totals.profit = formatNumber(groups[key].totals.profit + (m.profit || 0));
      
      const catName = m.expand?.category?.name || 'N/A';
      if (!groups[key].categoryTotals[catName]) {
        groups[key].categoryTotals[catName] = {
          name: catName,
          quantity: 0,
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0
        };
      }
      groups[key].categoryTotals[catName].quantity = formatNumber(groups[key].categoryTotals[catName].quantity + (m.totalQuantity || 0));
      groups[key].categoryTotals[catName].clientAmount = formatNumber(groups[key].categoryTotals[catName].clientAmount + (m.clientAmount || 0));
      groups[key].categoryTotals[catName].vendorAmount = formatNumber(groups[key].categoryTotals[catName].vendorAmount + (m.vendorAmount || 0));
      groups[key].categoryTotals[catName].profit = formatNumber(groups[key].categoryTotals[catName].profit + (m.profit || 0));
    });

    return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
  };

  // Get project summary with formatted numbers
  const getProjectSummary = () => {
    const filtered = measurements.filter(m => {
      const matchProject = filterProject === 'all' || m.project === filterProject;
      const matchVendor = filterVendor === 'all' || m.vendor === filterVendor;
      const matchSearch = m.expand?.project?.name?.toLowerCase().includes(search.toLowerCase()) ||
                          m.expand?.vendor?.name?.toLowerCase().includes(search.toLowerCase());
      return matchProject && matchVendor && matchSearch;
    });

    const summary = {};
    filtered.forEach(m => {
      const key = m.project;
      if (!summary[key]) {
        summary[key] = {
          projectId: m.project,
          projectName: m.expand?.project?.name || 'N/A',
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0,
          count: 0,
          vendors: {},
          categoryTotals: {}
        };
      }
      summary[key].clientAmount = formatNumber(summary[key].clientAmount + (m.clientAmount || 0));
      summary[key].vendorAmount = formatNumber(summary[key].vendorAmount + (m.vendorAmount || 0));
      summary[key].profit = formatNumber(summary[key].profit + (m.profit || 0));
      summary[key].count += 1;

      const vendorKey = m.vendor;
      if (!summary[key].vendors[vendorKey]) {
        summary[key].vendors[vendorKey] = {
          vendorName: m.expand?.vendor?.name || 'N/A',
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0,
          count: 0
        };
      }
      summary[key].vendors[vendorKey].clientAmount = formatNumber(summary[key].vendors[vendorKey].clientAmount + (m.clientAmount || 0));
      summary[key].vendors[vendorKey].vendorAmount = formatNumber(summary[key].vendors[vendorKey].vendorAmount + (m.vendorAmount || 0));
      summary[key].vendors[vendorKey].profit = formatNumber(summary[key].vendors[vendorKey].profit + (m.profit || 0));
      summary[key].vendors[vendorKey].count += 1;
      
      const catName = m.expand?.category?.name || 'N/A';
      if (!summary[key].categoryTotals[catName]) {
        summary[key].categoryTotals[catName] = {
          name: catName,
          quantity: 0,
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0
        };
      }
      summary[key].categoryTotals[catName].quantity = formatNumber(summary[key].categoryTotals[catName].quantity + (m.totalQuantity || 0));
      summary[key].categoryTotals[catName].clientAmount = formatNumber(summary[key].categoryTotals[catName].clientAmount + (m.clientAmount || 0));
      summary[key].categoryTotals[catName].vendorAmount = formatNumber(summary[key].categoryTotals[catName].vendorAmount + (m.vendorAmount || 0));
      summary[key].categoryTotals[catName].profit = formatNumber(summary[key].categoryTotals[catName].profit + (m.profit || 0));
    });

    return Object.values(summary);
  };

  // Get category wise summary with formatted numbers
  const getCategoryWiseSummary = () => {
    const filtered = measurements.filter(m => {
      const matchProject = filterProject === 'all' || m.project === filterProject;
      const matchVendor = filterVendor === 'all' || m.vendor === filterVendor;
      const matchCategory = filterCategory === 'all' || m.category === filterCategory;
      const matchDate = (!dateFrom || m.measurementDate >= dateFrom) && (!dateTo || m.measurementDate <= dateTo);
      return matchProject && matchVendor && matchCategory && matchDate;
    });

    const summary = {};
    filtered.forEach(m => {
      const catName = m.expand?.category?.name || 'N/A';
      const unit = categories.find(c => c.id === m.category)?.unit || '';
      const unitDisplay = getUnitDisplay(unit);
      
      if (!summary[catName]) {
        summary[catName] = {
          categoryId: m.category,
          categoryName: catName,
          unit: unitDisplay,
          totalQuantity: 0,
          deductionQuantity: 0,
          netQuantity: 0,
          clientAmount: 0,
          vendorAmount: 0,
          profit: 0,
          count: 0
        };
      }
      summary[catName].totalQuantity = formatNumber(summary[catName].totalQuantity + (m.totalQuantity || 0));
      summary[catName].deductionQuantity = formatNumber(summary[catName].deductionQuantity + (m.deductionQuantity || 0));
      summary[catName].netQuantity = formatNumber(summary[catName].netQuantity + (m.netQuantity || 0));
      summary[catName].clientAmount = formatNumber(summary[catName].clientAmount + (m.clientAmount || 0));
      summary[catName].vendorAmount = formatNumber(summary[catName].vendorAmount + (m.vendorAmount || 0));
      summary[catName].profit = formatNumber(summary[catName].profit + (m.profit || 0));
      summary[catName].count += 1;
    });

    return Object.values(summary);
  };

  // Toggle group expansion
  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading measurements...</p>
        </div>
      </div>
    );
  }

  const groupedData = getGroupedMeasurements();
  const projectSummary = getProjectSummary();
  const categoryWiseSummary = getCategoryWiseSummary();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <span>Site Measurements</span>
            <Badge className="bg-amber-100 text-amber-700 border-0">{measurements.length}</Badge>
          </h1>
          <p className="text-sm text-slate-500">Track site measurements, bills and profits</p>
        </div>
        <div className="flex gap-2">
          {selectedItems.length > 0 && (
            <Button 
              variant="destructive"
              onClick={handleBulkDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              <Trash className="w-4 h-4 mr-2" />
              Delete Selected ({selectedItems.length})
            </Button>
          )}
          <Button 
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-lg"
            onClick={openNewModal}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Measurement
          </Button>
        </div>
      </div>

      {/* Category Wise Summary Cards */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <PieChart className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-slate-700">Category-wise Summary</h3>
          <Badge className="bg-amber-100 text-amber-700">{categoryWiseSummary.length} Categories</Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categoryWiseSummary.map((cat) => (
            <div key={cat.categoryId} className="bg-gradient-to-r from-slate-50 to-white p-3 rounded-lg border border-slate-200">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-slate-800 text-sm">{cat.categoryName}</p>
                  <p className="text-xs text-slate-500">{cat.count} entries</p>
                </div>
                <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">{cat.unit}</Badge>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                <div>
                  <span className="text-slate-500">Total Qty:</span>
                  <span className="font-bold text-slate-700 ml-1">{formatDisplayNumber(cat.totalQuantity)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Net Qty:</span>
                  <span className="font-bold text-blue-600 ml-1">{formatDisplayNumber(cat.netQuantity)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Client:</span>
                  <span className="font-bold text-emerald-600 ml-1">Rs.{formatDisplayNumber(cat.clientAmount)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Profit:</span>
                  <span className={cn("font-bold ml-1", cat.profit >= 0 ? "text-green-600" : "text-red-600")}>
                    {cat.profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(cat.profit)}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {categoryWiseSummary.length === 0 && (
            <div className="col-span-full text-center py-4 text-slate-400 text-sm">
              No data available for category-wise summary
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Total Client Amount</p>
            <p className="text-2xl font-bold text-emerald-600">
              Rs.{formatDisplayNumber(measurements.reduce((sum, m) => sum + (m.clientAmount || 0), 0))}
            </p>
            <p className="text-xs text-slate-400">{measurements.length} entries</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Total Vendor Amount</p>
            <p className="text-2xl font-bold text-amber-600">
              Rs.{formatDisplayNumber(measurements.reduce((sum, m) => sum + (m.vendorAmount || 0), 0))}
            </p>
            <p className="text-xs text-slate-400">{measurements.length} entries</p>
          </CardContent>
        </Card>
        <Card className={cn("border-l-4", 
          measurements.reduce((sum, m) => sum + (m.profit || 0), 0) >= 0 ? "border-l-green-500" : "border-l-red-500"
        )}>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Total Profit</p>
            <p className={cn("text-2xl font-bold",
              measurements.reduce((sum, m) => sum + (m.profit || 0), 0) >= 0 ? "text-green-600" : "text-red-600"
            )}>
              {measurements.reduce((sum, m) => sum + (m.profit || 0), 0) >= 0 ? '+' : ''}
              Rs.{formatDisplayNumber(measurements.reduce((sum, m) => sum + (m.profit || 0), 0))}
            </p>
            <p className="text-xs text-slate-400">
              {measurements.reduce((sum, m) => sum + (m.clientAmount || 0), 0) > 0 
                ? ((measurements.reduce((sum, m) => sum + (m.profit || 0), 0) / measurements.reduce((sum, m) => sum + (m.clientAmount || 0), 0)) * 100).toFixed(1) 
                : 0}% margin
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Projects</p>
            <p className="text-2xl font-bold text-blue-600">{projectSummary.length}</p>
            <p className="text-xs text-slate-400">Active projects</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search projects, vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
          >
            <option value="all">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select
            value={filterVendor}
            onChange={(e) => setFilterVendor(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
          >
            <option value="all">All Vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
          >
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
            <span className="text-slate-400">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
          </div>
          <Button 
            variant="outline" 
            onClick={() => { setSearch(''); setFilterProject('all'); setFilterVendor('all'); setFilterCategory('all'); setDateFrom(''); setDateTo(''); }}
            className="text-slate-600"
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Project Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projectSummary.map((project) => (
          <Card key={project.projectId} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-slate-800">{project.projectName}</h3>
                  <p className="text-xs text-slate-500">{project.count} entries</p>
                </div>
                <Badge className={cn(
                  project.profit >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                )}>
                  {project.profit >= 0 ? 'Profit' : 'Loss'}
                </Badge>
              </div>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Client Amount:</span>
                  <span className="font-bold text-emerald-600">Rs.{formatDisplayNumber(project.clientAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Vendor Amount:</span>
                  <span className="font-bold text-amber-600">Rs.{formatDisplayNumber(project.vendorAmount)}</span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <span className="text-slate-600">Profit:</span>
                  <span className={cn("font-bold", project.profit >= 0 ? "text-green-600" : "text-red-600")}>
                    {project.profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(project.profit)}
                  </span>
                </div>
              </div>
              
              {/* Category wise breakdown inside project */}
              {Object.keys(project.categoryTotals).length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs font-medium text-slate-600 mb-1">Category-wise:</p>
                  <div className="space-y-0.5">
                    {Object.values(project.categoryTotals).map((cat) => (
                      <div key={cat.name} className="flex justify-between text-xs">
                        <span className="text-slate-500">{cat.name}</span>
                        <span className="font-medium text-slate-700">{formatDisplayNumber(cat.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-2">
                <Button variant="ghost" size="sm" className="text-xs text-blue-600">
                  <Users className="w-3 h-3 mr-1" />
                  {Object.keys(project.vendors).length} vendors
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Group View with Bulk Selection */}
      <div className="space-y-4">
        {groupedData.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <span className="text-4xl block mb-4">??</span>
              <p className="text-slate-500 font-medium">No measurements found</p>
              <p className="text-sm text-slate-400">Start by adding a new measurement</p>
              <Button 
                className="mt-4 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={openNewModal}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Measurement
              </Button>
            </CardContent>
          </Card>
        ) : (
          groupedData.map((group) => {
            const isExpanded = expandedGroups[`${group.date}_${group.project}_${group.vendor}`] !== false;
            const groupKey = `${group.date}_${group.project}_${group.vendor}`;
            
            return (
              <Card key={groupKey} className="overflow-hidden border-l-4 border-l-amber-400">
                {/* Group Header */}
                <div 
                  className="flex flex-wrap items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-white cursor-pointer hover:bg-slate-50/50"
                  onClick={() => toggleGroup(groupKey)}
                >
                  <div className="flex items-center gap-4">
                    <button className="text-slate-400">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    <div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-blue-100 text-blue-700 border-0">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(group.date)}
                        </Badge>
                        <Badge className="bg-purple-100 text-purple-700 border-0">
                          <Building2 className="w-3 h-3 mr-1" />
                          {group.projectName}
                        </Badge>
                        <Badge className="bg-amber-100 text-amber-700 border-0">
                          <Users className="w-3 h-3 mr-1" />
                          {group.vendorName}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{group.items.length} entries</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Client:</span>
                      <span className="font-bold text-emerald-600 ml-1">Rs.{formatDisplayNumber(group.totals.clientAmount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Vendor:</span>
                      <span className="font-bold text-amber-600 ml-1">Rs.{formatDisplayNumber(group.totals.vendorAmount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Profit:</span>
                      <span className={cn("font-bold ml-1", group.totals.profit >= 0 ? "text-green-600" : "text-red-600")}>
                        {group.totals.profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(group.totals.profit)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Group Content */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-2 text-xs font-semibold text-slate-600">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleSelectAll(); }}
                              className="hover:text-amber-600"
                            >
                              {selectAll ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                            </button>
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-600">Category</th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-600">Quantity</th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-600 text-right">Client Amount</th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-600 text-right">Vendor Amount</th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-600 text-right">Profit</th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-600 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {group.items.map(m => {
                          const category = categories.find(c => c.id === m.category);
                          const unit = category ? getUnitDisplay(category.unit) : '';
                          const isSelected = selectedItems.includes(m.id);
                          return (
                            <tr key={m.id} className="hover:bg-slate-50">
                              <td className="px-2 py-2 text-center">
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleSelectItem(m.id); }}
                                  className="hover:text-amber-600"
                                >
                                  {isSelected ? <CheckSquare className="w-4 h-4 text-amber-600" /> : <Square className="w-4 h-4" />}
                                </button>
                              </td>
                              <td className="px-4 py-2">
                                <Badge className="bg-blue-50 text-blue-700 border-0">
                                  {m.expand?.category?.name || 'N/A'}
                                </Badge>
                                <span className="text-xs text-slate-400 ml-1">({unit})</span>
                              </td>
                              <td className="px-4 py-2">{formatDisplayNumber(m.totalQuantity)} {unit}</td>
                              <td className="px-4 py-2 text-right font-mono text-emerald-600">Rs.{formatDisplayNumber(m.clientAmount)}</td>
                              <td className="px-4 py-2 text-right font-mono text-amber-600">Rs.{formatDisplayNumber(m.vendorAmount)}</td>
                              <td className={cn("px-4 py-2 text-right font-mono font-bold", m.profit >= 0 ? "text-green-600" : "text-red-600")}>
                                {m.profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(m.profit)}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <div className="flex items-center gap-1 justify-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    onClick={() => openEditModal(m)}
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDelete(m.id)}
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 font-semibold">
                        <tr>
                          <td colSpan="2" className="px-4 py-2 text-right font-medium">Group Total:</td>
                          <td className="px-4 py-2 font-bold">{formatDisplayNumber(group.items.reduce((sum, m) => sum + (m.totalQuantity || 0), 0))}</td>
                          <td className="px-4 py-2 text-right font-bold text-emerald-600">Rs.{formatDisplayNumber(group.totals.clientAmount)}</td>
                          <td className="px-4 py-2 text-right font-bold text-amber-600">Rs.{formatDisplayNumber(group.totals.vendorAmount)}</td>
                          <td className={cn("px-4 py-2 text-right font-bold", group.totals.profit >= 0 ? "text-green-600" : "text-red-600")}>
                            {group.totals.profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(group.totals.profit)}
                          </td>
                          <td></td>
                        </tr>
                        {/* Category-wise totals within group */}
                        {Object.keys(group.categoryTotals).length > 0 && (
                          <tr className="bg-slate-50/50">
                            <td colSpan="2" className="px-4 py-2 text-right text-xs text-slate-500">Category-wise:</td>
                            <td colSpan="5" className="px-4 py-2">
                              <div className="flex flex-wrap gap-2">
                                {Object.values(group.categoryTotals).map((cat) => (
                                  <Badge key={cat.name} className="bg-blue-50 text-blue-700 border-0 text-xs">
                                    {cat.name}: {formatDisplayNumber(cat.quantity)}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">
                    {editMode ? 'Edit Measurement' : 'New Measurement'}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {categoriesData.length} category(ies) � {categoriesData.reduce((sum, cat) => sum + cat.entries.length, 0)} entries
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    onClick={addCategory}
                    type="button"
                  >
                    <FolderPlus className="w-4 h-4 mr-1" />
                    Add Category
                  </Button>
                  <button 
                    onClick={closeModal}
                    className="text-slate-400 hover:text-slate-600 transition-colors p-2"
                    type="button"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {modalError && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
                  {modalError}
                </div>
              )}

              {modalSuccess && (
                <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm mb-4 border border-green-200">
                  ? {modalSuccess}
                </div>
              )}

              <form onSubmit={handleModalSubmit} className="space-y-4">
                {/* Common Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg border">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                    <Input
                      name="measurementDate"
                      type="date"
                      value={commonData.measurementDate}
                      onChange={handleCommonChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                    <select
                      name="project"
                      value={commonData.project}
                      onChange={handleCommonChange}
                      className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Vendor *</label>
                    <select
                      name="vendor"
                      value={commonData.vendor}
                      onChange={handleCommonChange}
                      className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                      required
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Categories */}
                {categoriesData.map((cat, catIndex) => {
                  const category = categories.find(c => c.id === cat.category);
                  const unit = category ? getUnitDisplay(category.unit) : '';
                  const showWidth = category?.unit === 'cum' || category?.unit === 'cft';
                  const showHeight = category?.unit !== 'rmt' && category?.unit !== 'rft';
                  const colSpan = showWidth && showHeight ? 3 : (showWidth || showHeight ? 2 : 1);
                  const categoryTotal = getCategoryTotal(cat);

                  return (
                    <div key={cat.id} className="border rounded-lg p-4 bg-white shadow-sm">
                      {/* Category Header */}
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <div className="flex-1 min-w-[150px]">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Category {catIndex + 1} *
                          </label>
                          <select
                            value={cat.category}
                            onChange={(e) => handleCategoryChange(catIndex, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                            required
                          >
                            <option value="">Select Category</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name} ({getUnitDisplay(c.unit)})</option>)}
                          </select>
                        </div>
                        <div className="w-32">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Client Rate *</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={cat.clientRate}
                            onChange={(e) => handleRateChange(catIndex, 'clientRate', e.target.value)}
                            className="text-sm"
                            placeholder="0.00"
                            required
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Rate *</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={cat.vendorRate}
                            onChange={(e) => handleRateChange(catIndex, 'vendorRate', e.target.value)}
                            className="text-sm"
                            placeholder="0.00"
                            required
                          />
                        </div>
                        <div className="flex gap-1 mt-6">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-green-500 text-green-600 hover:bg-green-50"
                            onClick={() => addEntryToCategory(catIndex)}
                            title="Add New Entry"
                            type="button"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeCategory(catIndex)}
                            title="Remove Category"
                            type="button"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Entries Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">#</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">Length</th>
                              {showWidth && <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">Width</th>}
                              {showHeight && <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">Height</th>}
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">Total Qty</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">Deduction</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600">Net Qty</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600 text-right">Client Amt</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600 text-right">Vendor Amt</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600 text-right">Profit</th>
                              <th className="px-2 py-1.5 text-xs font-semibold text-slate-600 text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {cat.entries.map((entry, entryIndex) => {
                              const showDeduction = entry.showDeduction || false;
                              return (
                                <tr key={entry.id} className="hover:bg-slate-50">
                                  <td className="px-2 py-1.5 text-center text-slate-500">{entryIndex + 1}</td>
                                  <td className="px-2 py-1.5">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={entry.length}
                                      onChange={(e) => handleEntryChange(catIndex, entryIndex, 'length', e.target.value)}
                                      className="w-20 text-sm"
                                      placeholder="0.00"
                                    />
                                  </td>
                                  {showWidth && (
                                    <td className="px-2 py-1.5">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={entry.width}
                                        onChange={(e) => handleEntryChange(catIndex, entryIndex, 'width', e.target.value)}
                                        className="w-20 text-sm"
                                        placeholder="0.00"
                                      />
                                    </td>
                                  )}
                                  {showHeight && (
                                    <td className="px-2 py-1.5">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={entry.height}
                                        onChange={(e) => handleEntryChange(catIndex, entryIndex, 'height', e.target.value)}
                                        className="w-20 text-sm"
                                        placeholder="0.00"
                                      />
                                    </td>
                                  )}
                                  <td className="px-2 py-1.5 text-center font-medium">
                                    {entry.totalQuantity > 0 ? `${formatDisplayNumber(entry.totalQuantity)}` : '-'}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {showDeduction ? (
                                      <div className="flex items-center gap-1">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={entry.deductionLength}
                                          onChange={(e) => handleEntryChange(catIndex, entryIndex, 'deductionLength', e.target.value)}
                                          className="w-16 text-sm"
                                          placeholder="L"
                                        />
                                        {showWidth && (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={entry.deductionWidth}
                                            onChange={(e) => handleEntryChange(catIndex, entryIndex, 'deductionWidth', e.target.value)}
                                            className="w-16 text-sm"
                                            placeholder="W"
                                          />
                                        )}
                                        {showHeight && (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={entry.deductionHeight}
                                            onChange={(e) => handleEntryChange(catIndex, entryIndex, 'deductionHeight', e.target.value)}
                                            className="w-16 text-sm"
                                            placeholder="H"
                                          />
                                        )}
                                        <span className="text-xs text-slate-400">
                                          {entry.deductionQuantity > 0 ? `= ${formatDisplayNumber(entry.deductionQuantity)}` : '-'}
                                        </span>
                                      </div>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                                        onClick={() => toggleDeduction(catIndex, entryIndex)}
                                        title="Add Deduction"
                                        type="button"
                                      >
                                        <MinusCircle className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-center font-bold text-blue-600">
                                    {entry.netQuantity > 0 ? `${formatDisplayNumber(entry.netQuantity)}` : '-'}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-medium text-emerald-600">
                                    {entry.clientAmount > 0 ? `Rs.${formatDisplayNumber(entry.clientAmount)}` : '-'}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-medium text-amber-600">
                                    {entry.vendorAmount > 0 ? `Rs.${formatDisplayNumber(entry.vendorAmount)}` : '-'}
                                  </td>
                                  <td className={cn("px-2 py-1.5 text-right font-medium", entry.profit >= 0 ? "text-green-600" : "text-red-600")}>
                                    {entry.profit !== 0 ? `${entry.profit >= 0 ? '+' : ''}Rs.${formatDisplayNumber(entry.profit)}` : '-'}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    <div className="flex items-center gap-1 justify-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1 h-auto"
                                        onClick={() => copyEntry(catIndex, entryIndex)}
                                        title="Copy"
                                        type="button"
                                      >
                                        <Copy className="w-3 h-3" />
                                      </Button>
                                      {showDeduction && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-green-600 hover:text-green-700 hover:bg-green-50 p-1 h-auto"
                                          onClick={() => toggleDeduction(catIndex, entryIndex)}
                                          title="Hide Deduction"
                                          type="button"
                                        >
                                          <EyeOff className="w-3 h-3" />
                                        </Button>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                                        onClick={() => removeEntryFromCategory(catIndex, entryIndex)}
                                        title="Remove"
                                        type="button"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-slate-50 font-semibold">
                            <tr>
                              <td colSpan={colSpan + 1} className="px-2 py-2 text-right">
                                Category Total:
                              </td>
                              <td className="px-2 py-2 text-center font-bold">
                                {formatDisplayNumber(categoryTotal.totalQuantity)}
                              </td>
                              <td className="px-2 py-2 text-center font-bold text-orange-600">
                                {formatDisplayNumber(categoryTotal.deductionQuantity)}
                              </td>
                              <td className="px-2 py-2 text-center font-bold text-blue-600">
                                {formatDisplayNumber(categoryTotal.netQuantity)}
                              </td>
                              <td className="px-2 py-2 text-right font-bold text-emerald-600">
                                Rs.{formatDisplayNumber(categoryTotal.clientAmount)}
                              </td>
                              <td className="px-2 py-2 text-right font-bold text-amber-600">
                                Rs.{formatDisplayNumber(categoryTotal.vendorAmount)}
                              </td>
                              <td className={cn("px-2 py-2 text-right font-bold", categoryTotal.profit >= 0 ? "text-green-600" : "text-red-600")}>
                                {categoryTotal.profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(categoryTotal.profit)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })}

                {/* Grand Total */}
                {getModalGrandTotal().netQuantity > 0 && (
                  <div className="bg-gradient-to-r from-emerald-50 to-amber-50 p-4 rounded-lg border border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Grand Total</h4>
                    <div className="grid grid-cols-5 gap-4 text-center">
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <p className="text-xs text-slate-500">Total Qty</p>
                        <p className="text-lg font-bold text-slate-700">{formatDisplayNumber(getModalGrandTotal().totalQuantity)}</p>
                      </div>
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <p className="text-xs text-slate-500">Deduction</p>
                        <p className="text-lg font-bold text-orange-600">{formatDisplayNumber(getModalGrandTotal().deductionQuantity)}</p>
                      </div>
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <p className="text-xs text-slate-500">Net Qty</p>
                        <p className="text-lg font-bold text-blue-600">{formatDisplayNumber(getModalGrandTotal().netQuantity)}</p>
                      </div>
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <p className="text-xs text-slate-500">Client Amount</p>
                        <p className="text-lg font-bold text-emerald-600">Rs.{formatDisplayNumber(getModalGrandTotal().clientAmount)}</p>
                      </div>
                      <div className="bg-white p-2 rounded-lg shadow-sm">
                        <p className="text-xs text-slate-500">Total Profit</p>
                        <p className={cn("text-lg font-bold", getModalGrandTotal().profit >= 0 ? "text-green-600" : "text-red-600")}>
                          {getModalGrandTotal().profit >= 0 ? '+' : ''}Rs.{formatDisplayNumber(getModalGrandTotal().profit)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-md"
                    disabled={submitting}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {submitting ? 'Saving...' : (editMode ? 'Update All' : `Save All (${categoriesData.reduce((sum, cat) => sum + cat.entries.length, 0)} entries)`)}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}